import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useApp } from "../context/AppContext";
import PageSeo from "../components/PageSeo";
import "./RidersDirectory.css";

const STATE_OPTIONS = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const STYLE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "commuter", label: "Commuter" },
  { value: "tourer", label: "Tourer" },
  { value: "offroad", label: "Off-road" },
  { value: "track", label: "Track" },
  { value: "adventure", label: "Adventure" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "followers", label: "Most Followers" },
  { value: "rides", label: "Most Rides" },
];

const toInitials = (value = "") => {
  if (!value) return "MH";
  const parts = value.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "MH";
};

export default function RidersDirectory() {
  const navigate = useNavigate();
  const { user: currentUser } = useApp();

  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [followerCounts, setFollowerCounts] = useState({});
  const [followingIds, setFollowingIds] = useState([]);
  const [locationStatus, setLocationStatus] = useState("idle");
  const [locationError, setLocationError] = useState("");
  const [detectedCity, setDetectedCity] = useState("");
  const [detectedState, setDetectedState] = useState("");
  const [nearbyEnabled, setNearbyEnabled] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [styleFilter, setStyleFilter] = useState("all");
  const [sortValue, setSortValue] = useState("newest");

  useEffect(() => {
    const fetchProfiles = async () => {
      setLoading(true);

      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("id, name, username, city, state, avatar_url, riding_style, bikes_owned, bio, created_at")
        .eq("is_public", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Failed to load riders", error);
        setProfiles([]);
        setFollowerCounts({});
        setLoading(false);
        return;
      }

      setProfiles(profileData || []);

      if (profileData?.length) {
        const profileIds = profileData.map((profile) => profile.id);
        const { data: followsData } = await supabase
          .from("follows")
          .select("follower_id, following_id")
          .in("following_id", profileIds);

        if (followsData) {
          const counts = followsData.reduce((acc, follow) => {
            acc[follow.following_id] = (acc[follow.following_id] || 0) + 1;
            return acc;
          }, {});
          setFollowerCounts(counts);

          if (currentUser?.id) {
            const currentFollowing = followsData
              .filter((follow) => follow.follower_id === currentUser.id)
              .map((follow) => follow.following_id);
            setFollowingIds(currentFollowing);
          } else {
            setFollowingIds([]);
          }
        }
      } else {
        setFollowerCounts({});
        setFollowingIds([]);
      }

      setLoading(false);
    };

    fetchProfiles();
  }, [currentUser?.id]);

  const isFollowing = (profileId) => followingIds.includes(profileId);

  const handleFollowToggle = async (event, profile) => {
    event.stopPropagation();
    if (!currentUser?.id || currentUser.id === profile.id) return;

    if (isFollowing(profile.id)) {
      await supabase.from("follows").delete().match({ follower_id: currentUser.id, following_id: profile.id });
      setFollowingIds((prev) => prev.filter((id) => id !== profile.id));
      setFollowerCounts((prev) => ({
        ...prev,
        [profile.id]: Math.max((prev[profile.id] || 1) - 1, 0),
      }));
      return;
    }

    await supabase.from("follows").insert({ follower_id: currentUser.id, following_id: profile.id });
    setFollowingIds((prev) => [...prev, profile.id]);
    setFollowerCounts((prev) => ({
      ...prev,
      [profile.id]: (prev[profile.id] || 0) + 1,
    }));
  };

  const normalizedDetectedCity = detectedCity.trim().toLowerCase();
  const nearbyMatches = useMemo(
    () =>
      normalizedDetectedCity
        ? profiles.filter((profile) => (profile.city || "").trim().toLowerCase() === normalizedDetectedCity)
        : [],
    [profiles, normalizedDetectedCity]
  );

  const filteredProfiles = useMemo(() => {
    let result = profiles;

    if (searchTerm.trim()) {
      const normalized = searchTerm.trim().toLowerCase();
      result = result.filter((profile) => {
        const nameMatch = profile.name?.toLowerCase().includes(normalized);
        const usernameMatch = profile.username?.toLowerCase().includes(normalized);
        return nameMatch || usernameMatch;
      });
    }

    if (stateFilter !== "all") {
      result = result.filter((profile) => (profile.state || "").toLowerCase() === stateFilter.toLowerCase());
    }

    if (styleFilter !== "all") {
      result = result.filter((profile) => profile.riding_style === styleFilter);
    }

    if (nearbyEnabled && normalizedDetectedCity) {
      result = result.filter((profile) => (profile.city || "").trim().toLowerCase() === normalizedDetectedCity);
    }

    return [...result].sort((a, b) => {
      if (sortValue === "newest") {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      if (sortValue === "followers") {
        const diff = (followerCounts[b.id] || 0) - (followerCounts[a.id] || 0);
        if (diff !== 0) return diff;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      const ridesA = a.rides_completed || 0;
      const ridesB = b.rides_completed || 0;
      if (ridesA === ridesB) {
        return (followerCounts[b.id] || 0) - (followerCounts[a.id] || 0);
      }
      return ridesB - ridesA;
    });
  }, [profiles, searchTerm, stateFilter, styleFilter, sortValue, followerCounts, nearbyEnabled, normalizedDetectedCity]);

  const handleDetectLocation = async () => {
    if (!navigator.geolocation) {
      setLocationError("Location services not supported on this device.");
      return;
    }

    setLocationStatus("loading");
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
            { headers: { "Accept-Language": "en-IN" } }
          );
          const data = await response.json();
          const address = data?.address || {};
          const resolvedCity = address.city || address.town || address.village || address.county || address.suburb || "";
          const resolvedState = address.state || address.state_district || "";

          if (!resolvedCity) {
            setLocationError("Could not detect your city. Try again later.");
            setLocationStatus("error");
            return;
          }

          setDetectedCity(resolvedCity);
          setDetectedState(resolvedState);
          setLocationStatus("success");
          setNearbyEnabled(true);
        } catch (reverseError) {
          console.error("Reverse geocoding failed", reverseError);
          setLocationError("Could not resolve your location. Try again later.");
          setLocationStatus("error");
        }
      },
      (geoError) => {
        console.error("Geolocation error", geoError);
        if (geoError.code === geoError.PERMISSION_DENIED) {
          setLocationError("Permission denied. Please allow location access to find nearby riders.");
        } else {
          setLocationError("Unable to detect location. Please try again.");
        }
        setLocationStatus("error");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  return (
    <div className="riders-page">
      <PageSeo
        title="Rider Community Directory | MotoHub India"
        description="Discover MotoHub India's rider community. Search and connect with tourers, commuters, off-roaders, and track riders across India."
        keywords={["MotoHub riders", "Indian bikers", "rider community", styleFilter !== "all" ? styleFilter : "motorcycle"]}
        path="/riders"
        image="/og-image.jpg"
      />

      <div className="riders-shell">
        <header className="riders-hero">
          <p className="riders-breadcrumb">MotoHub India Riders Directory</p>
          <h1 className="riders-title">Rider Community</h1>
          <p className="riders-subtitle">
            Discover riders across India, filter by style, and find your riding circle.
          </p>
          {locationStatus === "success" && normalizedDetectedCity ? (
            <div className="riders-nearby-pill">
              {nearbyMatches.length} riders in {detectedCity}
              {detectedState ? `, ${detectedState}` : ""}
            </div>
          ) : null}
          {locationError ? <div className="riders-error">{locationError}</div> : null}
        </header>

        <section className="riders-controls">
          <div className="riders-control">
            <label>Search</label>
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search name or @username"
              type="text"
            />
          </div>

          <div className="riders-control">
            <label>State</label>
            <select value={stateFilter} onChange={(event) => setStateFilter(event.target.value)}>
              <option value="all">All states</option>
              {STATE_OPTIONS.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="riders-control">
            <label>Riding Style</label>
            <select value={styleFilter} onChange={(event) => setStyleFilter(event.target.value)}>
              {STYLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="riders-control">
            <label>Sort</label>
            <select value={sortValue} onChange={(event) => setSortValue(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="riders-actions">
          <button onClick={handleDetectLocation} className="riders-btn" type="button">
            {locationStatus === "loading" ? "Detecting..." : "Detect Near Me"}
          </button>
          <button
            onClick={() => setNearbyEnabled((prev) => !prev)}
            disabled={!normalizedDetectedCity}
            className={`riders-btn ${nearbyEnabled && normalizedDetectedCity ? "riders-btn-active" : ""}`}
            type="button"
          >
            {nearbyEnabled && normalizedDetectedCity ? "Near Me On" : "Near Me"}
          </button>
          {normalizedDetectedCity ? <p className="riders-hint">Showing matches for {detectedCity}</p> : null}
        </section>

        {loading ? (
          <div className="riders-loading">Loading riders...</div>
        ) : filteredProfiles.length === 0 ? (
          <div className="riders-empty">No riders found. Try adjusting filters.</div>
        ) : (
          <section className="riders-grid">
            {filteredProfiles.map((profile) => {
              const followerCount = followerCounts[profile.id] || 0;
              return (
                <article
                  key={profile.id}
                  onClick={() => navigate(`/riders/${profile.username}`)}
                  className="rider-card"
                >
                  <div className="rider-avatar-wrap">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt={profile.name} className="rider-avatar" />
                    ) : (
                      <div className="rider-avatar rider-avatar-fallback">{toInitials(profile.name)}</div>
                    )}
                  </div>
                  <h3 className="rider-name">{profile.name}</h3>
                  <p className="rider-username">@{profile.username}</p>
                  <p className="rider-location">
                    {profile.city || "City"}
                    {profile.state ? `, ${profile.state}` : ""}
                  </p>
                  <p className="rider-meta">{followerCount} followers</p>

                  {currentUser?.id && currentUser.id !== profile.id ? (
                    <button
                      onClick={(event) => handleFollowToggle(event, profile)}
                      className={`rider-follow-btn ${isFollowing(profile.id) ? "rider-following" : ""}`}
                      type="button"
                    >
                      {isFollowing(profile.id) ? "Following" : "Follow"}
                    </button>
                  ) : (
                    <span className="rider-view-btn">View Profile</span>
                  )}
                </article>
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
}
